import React, { useRef, useEffect, useState } from 'react';
import '../App.css';
import { BsEasel, BsGear } from "react-icons/bs";
import { FcGoogle } from "react-icons/fc";

// Import Firebase services
import { getSongs } from '../services/songService';
import { getVideos, getInitialVideo } from '../services/videoService';
import { getPlaylists } from '../services/playlistService';
import { saveUserProject, getUserProject } from '../services/userService';
import { setupSessionTracking, updateFocusTimerValue } from '../services/sessionService';
import { Song, Video, Playlist } from '../types';

// Import custom hooks
import { useAudio } from '../hooks/useAudio';
import { useVideo } from '../hooks/useVideo';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import { useTimer } from '../hooks/useTimer';
import { useFullscreen } from '../hooks/useFullscreen';
import { usePlaylist } from '../hooks/usePlaylist';
import { useModal } from '../hooks/useModal';

// Import components
import ToolsPanel, { ToolType } from './ToolsPanel';
import ToolPicker from './ToolPicker';
import TypewriterModal from './TypewriterModal';
import Walkthrough, { WalkthroughStep } from './Walkthrough';
import SettingsModal from './SettingsModal';
import VideoBackground from './VideoBackground';
import AudioPlayer from './AudioPlayer';
import VideoSelector from './VideoSelector';
import PlayerControls from './PlayerControls';
import FocusTimer from './FocusTimer';
import ProjectOverlay from './ProjectOverlay';

// This component is a modified version of App.tsx that uses Firebase data instead of hardcoded arrays
function FirebaseApp() {
  // Data states
  const [songs, setSongs] = React.useState<Song[]>([]);
  const [videos, setVideos] = React.useState<Video[]>([]);
  const [playlists, setPlaylists] = React.useState<Playlist[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  
  // Video menu states
  const [isVideoMenuLoading, setIsVideoMenuLoading] = React.useState(false);
  const [isVideoMenuOpen, setIsVideoMenuOpen] = React.useState(false);
  const [preloadedThumbnails, setPreloadedThumbnails] = React.useState<{[key: string]: boolean}>({});
  const [allThumbnailsLoaded, setAllThumbnailsLoaded] = React.useState(false);
  
  // Initial welcome message
  const [showInitialWelcome, setShowInitialWelcome] = React.useState(() => {
    // Only show welcome on first visit
    const hasVisitedBefore = localStorage.getItem('lofiHasVisited');
    if (!hasVisitedBefore) {
      // Mark that user has visited
      localStorage.setItem('lofiHasVisited', 'true');
      return true;
    }
    return false;
  });
  
  // Login, welcome, and payment modals
  const [showLoginInfo, setShowLoginInfo] = React.useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = React.useState(false);
  
  // Use custom hooks
  const { user, authLoading, isNewUser, signInWithGoogle } = useAuth();
  const { 
    hasPaidSubscription, 
    isLoadingPayment, 
    isLoadingManageSubscription,
    showSubscriptionSuccess, 
    setShowSubscriptionSuccess,
    showSubscriptionCancel, 
    setShowSubscriptionCancel,
    redirectToPayment, 
    redirectToManageSubscription 
  } = useSubscription(user);
  
  const {
    currentSong,
    setCurrentSong,
    nextSong,
    setNextSong,
    isPaused,
    volume,
    setVolume,
    currentAudioRef,
    nextAudioRef,
    togglePlayPause,
    handleAudioEnded,
    startMusic
  } = useAudio(0.5);
  
  const {
    currentVideo,
    setCurrentVideo,
    nextVideo,
    setNextVideo,
    primaryVideoActive,
    setPrimaryVideoActive,
    isVideoFading,
    switchToVideo,
    preloadThumbnails
  } = useVideo();
  
  const [isPlaying, setIsPlaying] = React.useState(true);
  const { counter, formatTime } = useTimer(isPlaying, isPaused);
  
  // Update session service with the current timer value
  useEffect(() => {
    updateFocusTimerValue(counter);
  }, [counter]);
  
  const appContainerRef = useRef<HTMLDivElement>(null);
  const { isFullScreen, toggleFullScreen } = useFullscreen(appContainerRef as React.RefObject<HTMLElement>);
  
  const { 
    currentPlaylist, 
    setCurrentPlaylist,
    isPlaylistMenuOpen,
    setIsPlaylistMenuOpen,
    getRandomSong
  } = usePlaylist(songs);
  
  // Walkthrough state
  const [showWalkthrough, setShowWalkthrough] = React.useState(false);
  
  // Payment popup
  const [showPaymentPopup, setShowPaymentPopup] = React.useState(false);
  
  // Settings modal
  const [showSettingsModal, setShowSettingsModal] = React.useState(false);
  
  // Project overlay state
  const [showProjectOverlay, setShowProjectOverlay] = React.useState(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('vibeShowProjectOverlay');
    return saved ? JSON.parse(saved) : false;
  });
  const [currentProjectDescription, setCurrentProjectDescription] = React.useState<string>('');
  
  // Element refs for click-outside detection
  const playlistMenuRef = useRef<HTMLDivElement>(null);
  const playlistButtonRef = useRef<HTMLDivElement>(null);
  const videoMenuRef = useRef<HTMLDivElement>(null);
  const videoButtonRef = useRef<HTMLDivElement>(null);
  
  // Message constants
  const loginMessages = [ 
    "we're gonna to customise things for you",
    "but first you'll need an account"
  ];
  
  const welcomeMessages = [
    "this will only take a minute",
    "we'll customise the space for you",
    "+ give you the tour"
  ];
  
  const paymentMessages = [
    "theres a free trial, it's $10 a month, but you can cancel anytime. you'll get:",
    " - custom backgrounds and playlists",
    " - a notepad for ideas and todos",
    " - analytics to keep track of your progress",
    " - access to the lofizone community",
    "we'd love to have you along for the ride :)"
  ];
  
  const initialWelcomeMessages = [
    "this is a chill space for you to work on your own ideas",
  ];
  
  // Note-related state for walkthrough
  const [isNotePanelOpen, setIsNotePanelOpen] = useState(false);
  // Walkthrough demo mode - bypasses subscription checks during the tour
  const [isWalkthroughMode, setIsWalkthroughMode] = useState(false);
  
  // Tool picker and active tool state
  const [showToolPicker, setShowToolPicker] = useState(false);
  const [activeTool, setActiveTool] = useState<ToolType>(() => {
    // Load saved active tool from localStorage
    const savedTool = localStorage.getItem('vibeToolsPanelActiveTool') as ToolType;
    return savedTool && ['notes', 'chat'].includes(savedTool) ? savedTool : 'notes';
  });

  // Enabled tools state
  const [enabledTools, setEnabledTools] = useState<{ [key: string]: boolean }>(() => {
    // Load saved enabled tools from localStorage, default to all enabled
    const saved = localStorage.getItem('vibeEnabledTools');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved enabled tools:', e);
      }
    }
    // Default: all tools enabled
    return { notes: true, chat: true };
  });

  // Minimal mode state
  const [minimalMode, setMinimalMode] = useState<boolean>(() => {
    // Load saved minimal mode from localStorage, default to false
    const saved = localStorage.getItem('vibeMinimalMode');
    return saved === 'true';
  });

  // Mouse movement tracking for minimal mode
  const [isMouseMoving, setIsMouseMoving] = useState<boolean>(false);
  const [isHoveringInteractive, setIsHoveringInteractive] = useState<boolean>(false);
  const mouseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handler for toggling tool enabled state
  const handleToggleToolEnabled = (toolId: string, enabled: boolean) => {
    setEnabledTools(prev => {
      const updated = { ...prev, [toolId]: enabled };
      localStorage.setItem('vibeEnabledTools', JSON.stringify(updated));
      
      // If we're disabling the currently active tool, switch to another available tool or close panel
      if (!enabled && activeTool === toolId) {
        const availableTools = Object.keys(updated).filter(id => updated[id] !== false);
        if (availableTools.length > 0) {
          // Switch to the first available tool
          setActiveTool(availableTools[0] as ToolType);
        } else {
          // No tools available, close the panel
          setIsNotePanelOpen(false);
        }
      }
      
      return updated;
    });
  };

  // Handler for toggling minimal mode
  const handleToggleMinimalMode = (enabled: boolean) => {
    setMinimalMode(enabled);
    localStorage.setItem('vibeMinimalMode', String(enabled));
  };

  // Mouse movement tracking for minimal mode
  useEffect(() => {
    if (!minimalMode) return;

    const handleMouseMove = () => {
      setIsMouseMoving(true);
      
      // Clear existing timeout
      if (mouseTimeoutRef.current) {
        clearTimeout(mouseTimeoutRef.current);
      }
      
      // Set new timeout to hide UI after 500ms of no movement
      mouseTimeoutRef.current = setTimeout(() => {
        setIsMouseMoving(false);
      }, 500);
    };

    const handleMouseEnterInteractive = () => {
      setIsHoveringInteractive(true);
    };

    const handleMouseLeaveInteractive = () => {
      setIsHoveringInteractive(false);
    };

    // Add mouse move listener
    document.addEventListener('mousemove', handleMouseMove);

    // Add hover listeners to interactive elements
    const interactiveSelectors = [
      '.counter',
      '.background-switch-button',
      '.settings-button',
      '.player-controls',
      '.fullscreen-button',
      '.minimized-note',
      '.notes-panel',
      '.pause-play-button',
      '.now-playing',
      '.note-pin-button',
      '.tool-header-buttons'
    ];

    const interactiveElements: Element[] = [];
    
    interactiveSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        interactiveElements.push(element);
        element.addEventListener('mouseenter', handleMouseEnterInteractive);
        element.addEventListener('mouseleave', handleMouseLeaveInteractive);
      });
    });

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      
      // Clean up interactive element listeners
      interactiveElements.forEach(element => {
        element.removeEventListener('mouseenter', handleMouseEnterInteractive);
        element.removeEventListener('mouseleave', handleMouseLeaveInteractive);
      });
      
      // Clear timeout
      if (mouseTimeoutRef.current) {
        clearTimeout(mouseTimeoutRef.current);
      }
    };
  }, [minimalMode]);

  // Save active tool to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('vibeToolsPanelActiveTool', activeTool);
  }, [activeTool]);

  // Save panel open state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('vibeToolsPanelOpen', String(isNotePanelOpen));
  }, [isNotePanelOpen]);

  // Load panel open state from localStorage on mount
  useEffect(() => {
    const savedPanelState = localStorage.getItem('vibeToolsPanelOpen');
    if (savedPanelState === 'true') {
      setIsNotePanelOpen(true);
    }
  }, []);
  
  // Add this near other useRef declarations
  const notepadStepRendered = useRef(false);
  
  // Initialize session tracking on component mount
  useEffect(() => {
    // Setup session tracking as soon as the app loads
    setupSessionTracking();
  }, []);
  
  // Listen for auth state changes
  useEffect(() => {
    if (user && isNewUser && !showInitialWelcome) {
      // Show welcome popup after a short delay to let the UI update
      setTimeout(() => {
        setShowWelcomePopup(true);
      }, 500);
    }
    
    // If user is logged in, load their project from Firebase
    if (user && user.uid) {
      // Get their project description from Firebase
      getUserProject(user.uid).then(projectDescription => {
        if (projectDescription) {
          // Update localStorage with the Firebase version
          localStorage.setItem('vibeUserProject', projectDescription);
          setCurrentProjectDescription(projectDescription);
        }
      }).catch(error => {
        console.error('Error loading user project:', error);
      });
    }
  }, [user, isNewUser, showInitialWelcome]);
  
  // Load project description from localStorage on mount
  useEffect(() => {
    const savedProject = localStorage.getItem('vibeUserProject');
    if (savedProject) {
      setCurrentProjectDescription(savedProject);
    }
  }, []);
  
  // Listen for changes in localStorage (for when project is updated from settings)
  useEffect(() => {
    const handleStorageChange = () => {
      const savedProject = localStorage.getItem('vibeUserProject');
      if (savedProject) {
        setCurrentProjectDescription(savedProject);
      }
    };

    // Listen for storage events (when localStorage is updated from other tabs)
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for updates within the same tab
    const interval = setInterval(handleStorageChange, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);
  
  // Load data from Firebase
  useEffect(() => {
    const loadFirebaseData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch data from Firebase
        const [fetchedSongs, fetchedVideos, fetchedPlaylists, defaultVideo] = await Promise.all([
          getSongs(),
          getVideos(),
          getPlaylists(),
          getInitialVideo()
        ]);
        
        setSongs(fetchedSongs);
        setVideos(fetchedVideos);
        setPlaylists(fetchedPlaylists);
        
        // Preload all video thumbnails
        preloadThumbnails(fetchedVideos);
        
        // Check if there's a saved video ID in localStorage
        const savedVideoId = localStorage.getItem('vibeCurrentVideo');
        
        if (savedVideoId && fetchedVideos.length > 0) {
          // Find the saved video in the fetched videos
          const savedVideo = fetchedVideos.find(v => v.id === savedVideoId);
          
          // If we found the saved video, use it instead of the default
          if (savedVideo) {
            setCurrentVideo(savedVideo);
          } else {
            // If saved video wasn't found, use the default
            setCurrentVideo(defaultVideo);
          }
        } else {
          // No saved video, use the default
          setCurrentVideo(defaultVideo);
        }
        
        // Set initial playlist if available
        if (fetchedPlaylists.length > 0) {
          // Load saved playlist
          const savedPlaylistId = localStorage.getItem('vibeCurrentPlaylist');
          if (savedPlaylistId) {
            const savedPlaylist = fetchedPlaylists.find(p => p.id === savedPlaylistId);
            if (savedPlaylist) {
              setCurrentPlaylist(savedPlaylist);
            } else {
              setCurrentPlaylist(fetchedPlaylists[0]);
            }
          } else {
            setCurrentPlaylist(fetchedPlaylists[0]);
          }
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading data from Firebase:', err);
        setError('Failed to load data. Please try again later.');
        setIsLoading(false);
      }
    };
    
    loadFirebaseData();
  }, []);
  
  // Initialize the first song when data is loaded
  useEffect(() => {
    if (!isLoading && songs.length > 0 && currentPlaylist && !currentSong) {
      // Get a random song from the current playlist
      const firstSong = getRandomSong();
      if (firstSong) {
        setCurrentSong(firstSong);
        // Get a different song for the next song
        const secondSong = getRandomSong(firstSong.id);
        if (secondSong) {
            setNextSong(secondSong);
        } else if (songs.filter(s => currentPlaylist.songIds.includes(s.id)).length > 1) {
            // If only one other song, it might be the same, try again without exclusion if playlist has more than one song
            setNextSong(getRandomSong());
        }
        // If only one song in playlist, nextSong will remain null, which is fine.
      }
    }
  }, [isLoading, songs, currentPlaylist, getRandomSong, setCurrentSong, setNextSong, currentSong]);

  // Effect to queue the next song when currentSong is playing and nextSong is null
  useEffect(() => {
    if (currentSong && !nextSong && songs.length > 0 && currentPlaylist) {
      if (isPaused) return; // Don't queue next song if paused

      const playlistSongs = songs.filter(song => currentPlaylist.songIds.includes(song.id));
      if (playlistSongs.length <= 1) {
        // No other songs to play or only one song in the playlist, so no "next" song.
        return;
      }

      const newNextSong = getRandomSong(currentSong.id);
      if (newNextSong && newNextSong.id !== currentSong.id) {
        setNextSong(newNextSong);
      } else if (playlistSongs.length > 1) {
        // If getRandomSong returned current song or null, and there are other songs, try once more without exclusion
        // This handles the case where the only other song was picked by chance.
        const alternativeNextSong = playlistSongs.find(s => s.id !== currentSong.id);
        if (alternativeNextSong) {
            setNextSong(alternativeNextSong);
        }
      }
    }
  }, [currentSong, nextSong, songs, currentPlaylist, getRandomSong, setNextSong, isPaused]);
  
  // Handle clicks outside the playlist menu to close it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (isPlaylistMenuOpen && 
          playlistMenuRef.current && 
          !playlistMenuRef.current.contains(event.target as Node) &&
          !playlistButtonRef.current?.contains(event.target as Node)) {
        setIsPlaylistMenuOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPlaylistMenuOpen]);
  
  // Handle clicks outside the video menu to close it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (isVideoMenuOpen && 
          videoMenuRef.current && 
          !videoMenuRef.current.contains(event.target as Node) &&
          !videoButtonRef.current?.contains(event.target as Node)) {
        setIsVideoMenuOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVideoMenuOpen]);
  
  // Preload thumbnails function
  const handlePreloadThumbnails = (videos: Video[]) => {
    // Create an object to track which thumbnails have been loaded
    const loaded: {[key: string]: boolean} = {};
    const totalThumbnails = videos.filter(video => video.thumbnailUrl).length;
    let loadedCount = 0;
    
    videos.forEach(video => {
      if (video.thumbnailUrl) {
        // Create a new Image object for each thumbnail
        const img = new Image();
        
        // Set up load handler
        img.onload = () => {
          loaded[video.id] = true;
          loadedCount++;
          setPreloadedThumbnails(prev => ({...prev, [video.id]: true}));
          
          // Check if all thumbnails are loaded
          if (loadedCount === totalThumbnails) {
            setAllThumbnailsLoaded(true);
            setIsVideoMenuLoading(false);
          }
        };
        
        // Handle error case as well to not get stuck in loading state
        img.onerror = () => {
          loadedCount++;
          // Even if there's an error, mark it as loaded to not block the UI
          setPreloadedThumbnails(prev => ({...prev, [video.id]: true}));
          
          if (loadedCount === totalThumbnails) {
            setAllThumbnailsLoaded(true);
            setIsVideoMenuLoading(false);
          }
        };
        
        // Set source to start loading
        img.src = video.thumbnailUrl;
      }
    });
  };
  
  // Handler for switching background video button
  const handleBackgroundButtonClick = () => {
    // If we're in walkthrough mode, just open the menu without checks
    if (isWalkthroughMode) {
      if (!allThumbnailsLoaded) {
        setIsVideoMenuLoading(true);
        handlePreloadThumbnails(videos);
      }
      setIsVideoMenuOpen(!isVideoMenuOpen);
      return;
    }

    // Check if user is logged in before showing the video selection
    if (!user) {
      // Show the login information popup first
      setShowLoginInfo(true);
    } else if (!hasPaidSubscription) {
      // User is logged in but doesn't have a paid subscription
      // Show the payment popup
      setShowPaymentPopup(true);
    } else {
      // User is logged in and has a subscription, show the video selection menu
      if (!allThumbnailsLoaded) {
        // If thumbnails are not all loaded yet, show loading state
        setIsVideoMenuLoading(true);
        // Try to load them again just in case
        handlePreloadThumbnails(videos);
      }
      setIsVideoMenuOpen(!isVideoMenuOpen);
    }
  };
  
  // Handle playlist selection
  const handlePlaylistSelect = (playlist: Playlist) => {
    setCurrentPlaylist(playlist);
    // Get a new random song from this playlist
    const newSong = getRandomSong();
    // Only change if we got a valid song
    if (newSong) {
      setCurrentSong(newSong);
      setNextSong(getRandomSong(newSong.id));
      
      // Start playing the new song if music is already playing
      if (isPlaying && !isPaused && currentAudioRef.current) {
        // Reset the current audio
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        // After a short delay, play the new song
        setTimeout(() => {
          if (currentAudioRef.current) {
            currentAudioRef.current.play()
              .catch(e => console.error("Failed to play song after playlist change:", e));
          }
        }, 300);
      }
    }
    setIsPlaylistMenuOpen(false);
  };
  
  // Handle playlist button click
  const handlePlaylistButtonClick = () => {
    // If we're in walkthrough mode, just open the menu without checks
    if (isWalkthroughMode) {
      setIsPlaylistMenuOpen(!isPlaylistMenuOpen);
      return;
    }

    // Check if user is logged in before showing the playlist selection
    if (!user) {
      // Show the login information popup first
      setShowLoginInfo(true);
    } else if (!hasPaidSubscription) {
      // User is logged in but doesn't have a paid subscription
      // Show the payment popup
      setShowPaymentPopup(true);
    } else {
      // User is logged in and has a subscription, show the playlist menu
      setIsPlaylistMenuOpen(!isPlaylistMenuOpen);
    }
  };
  
  // Start walkthrough
  const startWalkthrough = () => {
    setShowWelcomePopup(false);
    setIsWalkthroughMode(true);
    setShowWalkthrough(true);
  };
  
  // Skip walkthrough
  const skipWalkthrough = () => {
    setShowWelcomePopup(false);
  };
  
  // Define setupNotepad function to set content in the notepad
  const setupNotepad = (content: string) => {
    // First open the notepad if it's not already open
    setIsNotePanelOpen(true);
    
    // Function to set up notepad content
    const setupNotepadContent = (attempts = 0) => {
      // Get the notepad element
      const notepadInput = document.querySelector('.editable-note') as HTMLDivElement;
      
      if (notepadInput) {
        console.log('Setting up notepad with content:', content);
        
        // Clear any existing content
        notepadInput.innerHTML = '';
        
        // Set the HTML content directly
        notepadInput.innerHTML = content;
        
        // Make sure checkboxes have proper event listeners
        const checkboxes = notepadInput.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach(checkbox => {
          checkbox.addEventListener('change', () => {
            // Ensure the 'checked' attribute reflects the checkbox state
            if ((checkbox as HTMLInputElement).checked) {
              checkbox.setAttribute('checked', 'checked');
            } else {
              checkbox.removeAttribute('checked');
            }
            
            // Save content when checkbox is toggled
            if (notepadInput) {
              localStorage.setItem('vibeNote', notepadInput.innerHTML);
            }
          });
        });
        
        // Trigger an input event to register the content
        notepadInput.dispatchEvent(new Event('input', { bubbles: true }));
        
        // Save to localStorage
        localStorage.setItem('vibeNote', content);
      } else if (attempts < 5) {
        // If notepad element is not found yet, retry a few times
        console.log('Notepad element not found, retrying...', attempts);
        setTimeout(() => setupNotepadContent(attempts + 1), 300);
      }
    };
    
    // Wait a bit longer for notepad to be fully ready then try to set it up
    setTimeout(setupNotepadContent, 500);
  };
  
  // Define walkthrough steps
  const walkthroughSteps: WalkthroughStep[] = [
    // Initial welcome and project input step
    {
      target: '.app-container',
      title: 'What are you building?',
      content: 'Tell us what you\'re building in just a few words.',
      prefix: 'I\'m building',
      placeholder: 'a web app for...',
      maxLength: 40,
      isInputStep: true,
      onActive: () => {
        // Make sure we're in a clean state
        setIsPlaylistMenuOpen(false);
        setIsVideoMenuOpen(false);
        setIsNotePanelOpen(false);
      },
      onExit: (userInput?: string) => {
        // Save the user's input for later use in the notepad
        if (userInput) {
          // Store the full project description with prefix in localStorage for future sessions
          const fullDescription = `I'm building ${userInput}`;
          localStorage.setItem('vibeUserProject', fullDescription);
          
          // Save to Firebase if user is logged in
          if (user && user.uid) {
            saveUserProject(user.uid, fullDescription)
              .catch(error => console.error('Failed to save project to Firebase:', error));
          }
        }
      }
    },
    // Timer step
    {
      target: '.counter',
      title: 'Spend some time on your idea',
      content: 'The second thing you need to do, is spend time on your idea. \n\n This timer helps you track how long you\'ve been locked in. Stay focused and watch your productivity grow!',
      position: 'bottom'
    },
    // Playlist steps
    {
      target: '.player-controls .now-playing',
      title: 'Change Your Playlist',
      content: 'Different people work better with different music.',
      position: 'right',
      onActive: () => {
        // Immediately open playlist menu for responsive walkthrough
        setIsPlaylistMenuOpen(true);
      },
      onExit: () => {
        // Hide the playlist menu when moving to next step
        setIsPlaylistMenuOpen(false);
      }
    },
    // Background steps
    {
      target: '.background-switch-button',
      title: 'Choose your vibe',
      content: "You're going to be spending a lot of time here, so make it feel like home.",
      position: 'left',
      onActive: () => {
        // Immediately open video menu for responsive walkthrough
        setIsVideoMenuOpen(true);
        
        // Handle thumbnail loading if needed
        if (!allThumbnailsLoaded) {
          setIsVideoMenuLoading(true);
          handlePreloadThumbnails(videos);
        }
      },
      onExit: () => {
        // Hide the video menu when moving to next step
        setIsVideoMenuOpen(false);
      }
    },
    // Final step - What are you working on today?
    {
      target: '.tools-panel.open',
      title: 'One thing to get done today',
      content: 'What\'s the one thing you want to get done today?',
      position: 'left',
      isInputStep: true,
      placeholder: "e.g. Set up a landing page",
      onActive: () => {
        // Open the notepad first without focus
        setIsNotePanelOpen(true);
        
        // Prevent the notepad from getting focus by focusing the walkthrough input field
        setTimeout(() => {
          const inputField = document.querySelector('.walkthrough-input') as HTMLInputElement;
          if (inputField) {
            inputField.focus();
          }
        }, 100);
      },
      onExit: (userInput?: string) => {
        // When leaving this step, create a simple todo
        if (userInput) {
          // Create a simple todo item with proper HTML structure
          const todoContent = `<input type="checkbox" class="note-checkbox">&nbsp;${userInput}`;
          
          // Set up the notepad with just this one todo
          setupNotepad(todoContent);
        }
      }
    },
    // Additional information about slash commands
    {
      target: '.tools-panel.open',
      title: 'Slash Commands',
      content: 'The notepad supports two commands:\n\nType "/todo" to create a checkbox for task tracking.\n\n To fill the notepad using a prompt, type your input followed by "/prompt"',
      position: 'left',
      onActive: () => {
        // Keep the notepad open
        setIsNotePanelOpen(true);
      }
    },
    // Community chat showcase
    {
      target: '.tools-panel.open',
      title: 'Connect with Creators',
      content: 'Join the community chat to connect with other focused creators and share what you\'re working on.',
      position: 'left',
      onActive: () => {
        // Switch to the chat tool and keep the panel open
        setActiveTool('chat');
        setIsNotePanelOpen(true);
      }
    }
  ];
  
  // Close all open menus when walkthrough ends
  const handleWalkthroughClose = () => {
    setShowWalkthrough(false);
    setIsPlaylistMenuOpen(false);
    setIsVideoMenuOpen(false);
    setIsNotePanelOpen(false);
    setIsWalkthroughMode(false); // Turn off walkthrough mode
    setShowPaymentPopup(true);
  };
  
  // Handler for toggling project overlay
  const handleToggleProjectOverlay = (show: boolean) => {
    setShowProjectOverlay(show);
    localStorage.setItem('vibeShowProjectOverlay', JSON.stringify(show));
  };
  
  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }
  
  if (error) {
    return <div className="error-screen">
      <h2>Error</h2>
      <p>{error}</p>
      <button onClick={() => window.location.reload()}>Retry</button>
    </div>;
  }

  // Determine currently active video
  const activeVideoId = primaryVideoActive 
    ? currentVideo?.id 
    : nextVideo?.id;

  return (
    <div 
      ref={appContainerRef} 
      className={`app-container ${minimalMode ? 'minimal-mode' : ''} ${minimalMode && (isMouseMoving || isHoveringInteractive) ? 'show-ui' : ''}`}
      tabIndex={0}
    >
      {/* Focus Timer */}
      <FocusTimer
        formattedTime={formatTime(counter)}
        isFullScreen={isFullScreen}
        onToggleFullScreen={toggleFullScreen}
      />
      
      {/* Background Switch Button */}
      <div 
        className="background-switch-button" 
        onClick={handleBackgroundButtonClick}
        ref={videoButtonRef}
      >
        <BsEasel/>
      </div>
      
      {/* Settings Button */}
      <div 
        className="settings-button"
        onClick={() => setShowSettingsModal(true)}
        title="Settings"
      >
        <BsGear/>
      </div>
      
      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        volume={volume}
        onVolumeChange={setVolume}
        onManageSubscription={redirectToManageSubscription}
        isLoadingManageSubscription={isLoadingManageSubscription}
        hasPaidSubscription={hasPaidSubscription}
        showProjectOverlay={showProjectOverlay}
        onToggleProjectOverlay={handleToggleProjectOverlay}
        enabledTools={enabledTools}
        onToggleToolEnabled={handleToggleToolEnabled}
        minimalMode={minimalMode}
        onToggleMinimalMode={handleToggleMinimalMode}
      />
      
      {/* Initial welcome message */}
      <TypewriterModal
        isOpen={showInitialWelcome}
        onClose={() => {
          setShowInitialWelcome(false);
          startMusic();
        }}
        onPrimaryAction={() => {
          setShowInitialWelcome(false);
          startMusic();
        }}
        title="Welcome to lofizone"
        messages={initialWelcomeMessages}
        primaryButtonText="Get Started"
        className="welcome-modal"
      />
      
      {/* Login modal */}
      <TypewriterModal
        isOpen={showLoginInfo}
        onClose={() => setShowLoginInfo(false)}
        onPrimaryAction={() => {
          setShowLoginInfo(false);
          signInWithGoogle();
        }}
        title="Customising Your Space"
        messages={loginMessages}
        primaryButtonText="Sign in with Google"
        primaryButtonIcon={<FcGoogle />}
        secondaryButtonText="Skip customization"
        className="payment-modal"
      />
      
      {/* Welcome popup after authentication */}
      <TypewriterModal
        isOpen={showWelcomePopup}
        onClose={skipWalkthrough}
        onPrimaryAction={startWalkthrough}
        title="Let's Show You Around"
        messages={welcomeMessages}
        primaryButtonText="Show me around"
        className="welcome-modal"
      />
      
      {/* Payment popup */}
      <TypewriterModal
        isOpen={showPaymentPopup}
        onClose={() => setShowPaymentPopup(false)}
        onPrimaryAction={redirectToPayment}
        title="Unlock Full Access"
        messages={paymentMessages}
        primaryButtonText="Unlock Full Access"
        primaryButtonDisabled={isLoadingPayment}
        secondaryButtonText="Continue without customization"
        className="payment-modal"
      />
      
      {/* Walkthrough component */}
      <Walkthrough
        isOpen={showWalkthrough}
        onClose={handleWalkthroughClose}
        steps={walkthroughSteps}
        setupNotepad={setupNotepad}
      />
      
      {/* Authentication Loading Modal */}
      {authLoading && (
        <div className="auth-modal">
          <div className="auth-modal-content">
            <div className="auth-loader"></div>
            <p>Logging in...</p>
          </div>
        </div>
      )}
      
      {/* Video Selection Menu */}
      <VideoSelector
        isVideoMenuOpen={isVideoMenuOpen}
        videos={videos}
        activeVideoId={activeVideoId}
        isVideoMenuLoading={isVideoMenuLoading}
        onVideoSelect={switchToVideo}
        onClose={() => setIsVideoMenuOpen(false)}
        menuRef={videoMenuRef as React.RefObject<HTMLDivElement>}
        isWalkthroughMode={isWalkthroughMode} // Pass walkthrough mode
      />
      
      {/* Video Background */}
      <VideoBackground
        currentVideo={currentVideo}
        nextVideo={nextVideo}
        primaryVideoActive={primaryVideoActive}
        isVideoFading={isVideoFading}
      />
      
      {/* Project Overlay */}
      <ProjectOverlay
        isVisible={showProjectOverlay}
        projectDescription={currentProjectDescription}
      />
      
      {/* Audio Player */}
      <AudioPlayer
        currentSong={currentSong}
        nextSong={nextSong}
        currentAudioRef={currentAudioRef as React.RefObject<HTMLAudioElement>}
        nextAudioRef={nextAudioRef as React.RefObject<HTMLAudioElement>}
        onAudioEnded={handleAudioEnded}
      />
      
      {/* Player Controls */}
      <PlayerControls
        currentPlaylist={currentPlaylist}
        isPaused={isPaused}
        currentSong={!!currentSong}
        isPlaylistMenuOpen={isPlaylistMenuOpen}
        onPlaylistButtonClick={handlePlaylistButtonClick}
        onTogglePlayPause={togglePlayPause}
        onPlaylistSelect={handlePlaylistSelect}
        playlists={playlists}
        playlistButtonRef={playlistButtonRef as React.RefObject<HTMLDivElement>}
        playlistMenuRef={playlistMenuRef as React.RefObject<HTMLDivElement>}
        isWalkthroughMode={isWalkthroughMode} // Pass walkthrough mode
      />
      
      {/* Notes Panel */}
      <ToolsPanel 
        user={user}
        hasPaidSubscription={hasPaidSubscription}
        onShowPaymentPopup={() => setShowPaymentPopup(true)}
        onShowLoginInfo={() => setShowLoginInfo(true)}
        isOpen={isNotePanelOpen}
        onToggle={setIsNotePanelOpen}
        activeTool={activeTool}
        onShowToolPicker={() => setShowToolPicker(true)}
        onToolSelect={(toolId) => {
          setActiveTool(toolId);
          setIsNotePanelOpen(true); // Open the panel when a tool is selected
        }}
        isWalkthroughMode={isWalkthroughMode} // Pass walkthrough mode
        enabledTools={enabledTools}
      />
      
      {/* Subscription Success Notification */}
      <TypewriterModal
        isOpen={showSubscriptionSuccess}
        onClose={() => setShowSubscriptionSuccess(false)}
        onPrimaryAction={() => setShowSubscriptionSuccess(false)}
        title="Welcome to the Full Experience"
        messages={[
          "Thank you for subscribing to lofizone!",
          "You now have full access to all features.",
          "Ready to get started?"
        ]}
        primaryButtonText="Let's go"
        className="success-modal"
      />
      
      {/* Subscription Cancel Notification */}
      <TypewriterModal
        isOpen={showSubscriptionCancel}
        onClose={() => setShowSubscriptionCancel(false)}
        onPrimaryAction={() => setShowSubscriptionCancel(false)}
        title="Subscription Cancelled"
        messages={[
          "You've cancelled the subscription process.",
          "You can try again anytime."
        ]}
        primaryButtonText="Got it"
        className="cancel-modal"
      />

      {/* Tool Picker Overlay */}
      <ToolPicker
        isOpen={showToolPicker}
        onClose={() => setShowToolPicker(false)}
        activeTool={activeTool}
        onToolSelect={(toolId) => {
          setActiveTool(toolId);
          setIsNotePanelOpen(true); // Open the panel when a tool is selected
        }}
        user={user}
        hasPaidSubscription={hasPaidSubscription}
        onShowPaymentPopup={() => setShowPaymentPopup(true)}
        onShowLoginInfo={() => setShowLoginInfo(true)}
        isWalkthroughMode={isWalkthroughMode}
        enabledTools={enabledTools}
      />
    </div>
  );
}

export default FirebaseApp; 